// Phase 2e.13 — In-app lightbox for previewing block media.
//
// Solves three real problems we'd hit using anchor + target="_blank":
//
//   1. Chrome (since v60) blocks top-level navigation to data: URLs as
//      anti-phishing. Clicking a data-URL anchor lands on about:blank
//      with no content. That's the "photos open to a blank page" bug.
//
//   2. Even on browsers that allow the navigation, opening a multi-MB
//      data URL in a new tab is slow and burns a full tab. The teacher
//      flow is "glance at the resource, close, continue" — better
//      served by a modal that opens instantly and dismisses on ESC.
//
//   3. Cover teachers should NOT end up with documents on their device.
//      A lightbox keeps the file inside the page; no "save as", no
//      browser-native download bar.
//
// Strategy:
//
//   • Images: `<img src="data:…">` — always works, no conversion.
//   • PDFs: convert the base64 data URL to a Blob URL and render in an
//     `<iframe>`. Chrome blocks data URLs in iframes too; Blob URLs
//     are not subject to the same block. We revoke the URL on unmount.
//   • Other types (docx/xlsx/etc.): there is no in-browser renderer
//     for these without a third-party library. We render a friendly
//     placeholder explaining the limit. If `allowDownload` is true
//     we offer a Download button as the escape hatch; cover-teacher
//     mode (`allowDownload=false`) hides it entirely.
//
// The modal is portalled to document.body to escape any stacking
// context (sticky ancestors in the admin pending preview create their
// own contexts and would otherwise let modal content bleed through).

import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, FileText, Loader2 } from 'lucide-react';
import { useBlobReadUrl } from '@/lib/use-blob-read-url';

export interface MediaLightboxItem {
  /** Display name shown in the header. */
  name: string;
  /** Either `dataUrl` (legacy inline) OR `blobPath` (new direct-to-blob) is required.
   *  Blob-stored files resolve to a SAS URL at mount via useBlobReadUrl. */
  dataUrl?: string;
  blobPath?: string;
  /** MIME type. Optional — falls back to detection from the URL or extension. */
  type?: string;
}

interface MediaLightboxProps {
  item: MediaLightboxItem | null;
  /** Hide the Download button when the viewer is rendered for the
   *  cover teacher (view-only by product decision). */
  allowDownload: boolean;
  onClose: () => void;
}

export function MediaLightbox({ item, allowDownload, onClose }: MediaLightboxProps) {
  // For blob-stored items, fetch a short-lived SAS URL. The hook
  // returns null until resolved; we render a spinner during that
  // window. dataUrl items skip this and render immediately.
  const { url: sasUrl, loading: sasLoading } = useBlobReadUrl(
    item?.blobPath && !item?.dataUrl ? item.blobPath : undefined,
    item?.name,
  );

  // Build a Blob URL for non-image content (PDFs in particular)
  // when the source is a data URL. Chrome blocks iframe src from
  // data: URLs; URL.createObjectURL output is not blocked. For
  // blob-stored (SAS URL) content, the URL is already a real
  // https URL — no conversion needed.
  const blobUrl = useMemo(() => {
    if (!item || !item.dataUrl) return null;
    const mime = detectMime(item, item.dataUrl);
    if (mime.startsWith('image/')) return null;
    try {
      return dataUrlToBlobUrl(item.dataUrl);
    } catch (err) {
      console.warn('[MediaLightbox] Could not convert data URL to blob:', err);
      return null;
    }
  }, [item]);

  // Revoke the Blob URL on unmount / item change to free memory.
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  // ESC dismisses the modal — standard modal pattern.
  useEffect(() => {
    if (!item) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [item, onClose]);

  if (!item) return null;
  if (typeof document === 'undefined') return null;

  // Resolved source URL: data URL straight from the item, SAS URL
  // resolved from the blob path, or null while loading.
  const srcUrl: string | null = item.dataUrl ?? sasUrl ?? null;
  const mime = detectMime(item, srcUrl ?? undefined);
  const isImage = mime.startsWith('image/');
  const isPdf = mime === 'application/pdf' || /\.pdf$/i.test(item.name);
  const canPreviewInline = isImage || isPdf;
  // For PDFs we either have a Blob URL (from data URL conversion)
  // or the SAS URL (when blob-stored). Either works as iframe src.
  const pdfSrc = blobUrl ?? sasUrl;
  // Anchor href + download attribute work for both data URLs and
  // SAS URLs. For blob-stored we ALSO set target=_blank since the
  // SAS URL points to an external host (storage account).
  const downloadHref = srcUrl;

  return createPortal(
    <div
      // z-[10500] sits above the tutorial bar (z-10000/10001). It does NOT
      // sit above the demo banner, which pins itself at the top of the
      // stacking order on purpose, and for a while that banner painted over
      // this header: the X and the Download button were there, under sixty
      // pixels of orange, and the preview looked like it had no way out. The
      // header now starts beneath the banner instead, using the height the
      // banner publishes for exactly this purpose (usePublishedHeight, kept
      // live by a ResizeObserver), the same way ClassEditor and the wizards
      // offset themselves. Outside the demo the variable is unset and the
      // fallback is 0px.
      className="fixed inset-0 z-[10500] bg-black/85 backdrop-blur-sm flex flex-col"
      style={{ paddingTop: 'var(--cmc-demo-banner-height, 0px)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Preview ${item.name}`}
    >
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 bg-black/40 border-b border-white/10"
        onClick={e => e.stopPropagation()}
      >
        <FileText className="w-4 h-4 text-white/80 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">{item.name}</div>
        </div>
        {allowDownload && downloadHref && (
          <a
            href={downloadHref}
            download={item.name}
            target={item.blobPath ? '_blank' : undefined}
            rel={item.blobPath ? 'noopener noreferrer' : undefined}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Download
          </a>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="p-2 rounded-lg text-white/80 hover:bg-white/10 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div
        className="flex-1 min-h-0 flex items-center justify-center p-4 overflow-auto"
        onClick={e => e.stopPropagation()}
      >
        {sasLoading && !srcUrl && (
          <div className="flex flex-col items-center gap-3 text-white/80">
            <Loader2 className="w-7 h-7 animate-spin" />
            <span className="text-sm">Loading preview…</span>
          </div>
        )}

        {isImage && srcUrl && (
          <img
            src={srcUrl}
            alt={item.name}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
          />
        )}

        {isPdf && pdfSrc && (
          <iframe
            src={pdfSrc}
            title={item.name}
            className="w-full h-full rounded-lg bg-white shadow-2xl"
            style={{ minHeight: '70vh' }}
          />
        )}

        {!canPreviewInline && srcUrl && (
          <div className="max-w-md text-center text-white/90 bg-black/40 rounded-2xl p-8 border border-white/10">
            <FileText className="w-12 h-12 mx-auto mb-3 text-white/60" />
            <h3 className="text-lg font-bold text-white mb-1">No in-app preview for this file type</h3>
            <p className="text-sm text-white/70 leading-relaxed mb-5">
              {item.name.split('.').pop()?.toUpperCase() || 'This file format'} can't be rendered inside the browser.
              {allowDownload
                ? ' Use the Download button above to open it on your device.'
                : ' Ask the admin to share it through Teams or SharePoint.'}
            </p>
            {/* Belt-and-braces close button inside the message card, in
                addition to the header X and Escape key. The header can
                sit behind page chrome on older deploys / odd viewports;
                an in-content exit guarantees the user is never stuck. */}
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              {allowDownload && downloadHref && (
                <a
                  href={downloadHref}
                  download={item.name}
                  target={item.blobPath ? '_blank' : undefined}
                  rel={item.blobPath ? 'noopener noreferrer' : undefined}
                  className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-white text-sm font-semibold transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download
                </a>
              )}
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:brightness-110 text-sm font-semibold transition-all"
              >
                <X className="w-4 h-4" />
                Close preview
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Best-effort MIME detection. `item.type` from the file picker is
 *  the most reliable source; fall back to parsing the data URL prefix
 *  for legacy lessons; finally fall back to file extension. SAS URLs
 *  carry no MIME hint in the URL itself, so they always rely on
 *  item.type or extension. */
function detectMime(item: MediaLightboxItem, src?: string): string {
  if (item.type) return item.type;
  if (src && src.startsWith('data:')) {
    const prefix = src.match(/^data:([^;]+);/);
    if (prefix?.[1]) return prefix[1];
  }
  const ext = item.name.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return 'application/pdf';
  if (ext && ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) {
    return `image/${ext === 'jpg' ? 'jpeg' : ext}`;
  }
  return 'application/octet-stream';
}

/** Convert a base64 data URL into a Blob URL. `URL.createObjectURL` is
 *  not subject to Chrome's data-URL navigation block, so it works for
 *  iframe src (PDF) and anchor target="_blank" alike. */
function dataUrlToBlobUrl(dataUrl: string): string {
  const commaIdx = dataUrl.indexOf(',');
  if (commaIdx === -1) throw new Error('Malformed data URL');
  const meta = dataUrl.slice(5, commaIdx);
  const payload = dataUrl.slice(commaIdx + 1);
  const isBase64 = meta.endsWith(';base64');
  const mime = (isBase64 ? meta.slice(0, -7) : meta) || 'application/octet-stream';

  let bytes: Uint8Array;
  if (isBase64) {
    const binary = atob(payload);
    bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  } else {
    // Non-base64 (text) payload — decode via percent-decoding.
    const decoded = decodeURIComponent(payload);
    bytes = new TextEncoder().encode(decoded);
  }

  const blob = new Blob([bytes], { type: mime });
  return URL.createObjectURL(blob);
}
